import React from 'react';
import {
  FiLock,
  FiShield,
  FiEye,
  FiUserX
} from 'react-icons/fi';
import { useLanguage } from '../Context/LanguageContext';
import './PermissionNotice.css';

/**
 * Reusable component for displaying permission notices to users
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the notice
 * @param {'view-only'|'admin-only'|'custom'} props.type - Type of permission notice
 * @param {string} props.title - Custom title for the notice
 * @param {string} props.message - Custom message for the notice
 * @param {string} props.variant - Bootstrap alert variant (info, warning, danger)
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.icon - Custom icon element
 * @param {React.ReactNode} props.children - Custom content
 */
const PermissionNotice = ({
  show = true,
  type = 'view-only',
  title,
  message,
  variant = 'info',
  className = '',
  icon,
  children,
  ...props
}) => {
  const { t } = useLanguage();

  if (!show) return null;

  // Default configurations for different notice types
  const noticeConfigs = {
    'view-only': {
      title: t('components.permissionnotice.viewOnlyTitle'),
      message: t('components.permissionnotice.viewOnlyMessage'),
      icon: <FiEye size={20} />,
      variant: 'info'
    },
    'admin-only': {
      title: t('components.permissionnotice.adminOnlyTitle'),
      message: t('components.permissionnotice.adminOnlyMessage'),
      icon: <FiShield size={20} />,
      variant: 'warning'
    },
    'restricted': {
      title: t('components.permissionnotice.restrictedTitle'),
      message: t('components.permissionnotice.restrictedMessage'),
      icon: <FiLock size={20} />,
      variant: 'warning'
    },
    'no-access': {
      title: t('components.permissionnotice.noAccessTitle'),
      message: t('components.permissionnotice.noAccessMessage'),
      icon: <FiUserX size={20} />,
      variant: 'danger'
    },
    'custom': {
      title: title || t('components.permissionnotice.customTitle'),
      message: message || t('components.permissionnotice.customMessage'),
      icon: icon || <FiLock size={20} />,
      variant: variant
    }
  };

  const config = noticeConfigs[type] || noticeConfigs['view-only'];
  const displayTitle = title || config.title;
  const displayMessage = message || config.message;
  const displayIcon = icon || config.icon;

  return (
    <div className={`permission-notice mb-4 p-3 rounded-3 bg-info-light ${className}`} role="alert" {...props}>
      <div className="d-flex">
        <div className="info-icon me-3">
          {displayIcon}
        </div>
        <div>
          <h6 className="mb-1 text-primary-dark">{displayTitle}</h6>
          {(displayMessage || children) && (
            <p className="mb-0 small text-dark">
              {children || displayMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionNotice;